import React, { useEffect, useRef, useState } from 'react';
import './common.scss';

// Props:
//   value       currently selected value
//   onChange    (value) => void
//   options     [{ value, label }]
//   placeholder shown when nothing is selected (default "Select…")
//   disabled    boolean
//   ariaLabel   accessible name for the trigger
//   minWidth    trigger min width (px, default 150)
//   style       passthrough on the wrapper
// ─────────────────────────────────────────────────────────────

export default function CommonDropdown({
    value,
    onChange,
    options = [],
    placeholder = 'Select…',
    disabled = false,
    ariaLabel,
    minWidth = 150,
    style,
}) {
    const [open, setOpen] = useState(false);
    const boxRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    const selected = options.find((o) => o.value === value);

    return (
        <div ref={boxRef} className="common_dropdown" style={{ minWidth, ...style }}>
            <button
                type="button"
                className={`common_dropdown__control${open ? ' open' : ''}${selected ? '' : ' is-placeholder'}`}
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-expanded={open}
                disabled={disabled}
                onClick={() => setOpen((o) => !o)}
            >
                <span className="common_dropdown__label">
                    {selected ? selected.label : placeholder}
                </span>
                <span className={`common_dropdown__caret${open ? ' open' : ''}`}>▾</span>
            </button>

            {open && (
                <div className="common_dropdown__menu" role="listbox">
                    {options.map((o) => {
                        const isActive = o.value === value;
                        return (
                            <div
                                key={o.value ?? o.label}
                                role="option"
                                aria-selected={isActive}
                                onClick={() => { onChange(o.value); setOpen(false); }}
                                className={`common_dropdown__option${isActive ? ' active' : ''}`}
                            >
                                {o.label}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
